import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TierBadge } from "@/components/LoyaltyBadge";
import { Loader2, Star, Gift } from "lucide-react";
import { toast } from "sonner";

export default function Loyalty() {
  const [data, setData] = useState(null);
  const [redeeming, setRedeeming] = useState(null);

  const load = () => api.get("/loyalty").then(({ data }) => setData(data));

  useEffect(() => {
    load();
  }, []);

  const redeem = async (customerId) => {
    setRedeeming(customerId);
    try {
      await api.post(`/loyalty/${customerId}/redeem`);
      toast.success("Free night redeemed");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Redeem failed");
    } finally {
      setRedeeming(null);
    }
  };

  if (!data) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#A0148C]" />
      </div>
    );
  }

  const { members, config } = data;
  const enabled = config.enabled;

  return (
    <div className="px-4 py-6 md:px-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-800">
          <Star className="h-6 w-6 text-[#A0148C]" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-50">Loyalty</h1>
          <p className="text-slate-400">
            {enabled ? (
              <>
                Earn {config.earnRate} pts {config.earnMode === "perNight" ? "per night" : "per $1"} · {config.threshold} pts = 1 free night
              </>
            ) : (
              "Loyalty program is currently disabled in Settings."
            )}
          </p>
        </div>
      </div>

      <Card className="border-slate-800 bg-slate-900 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead className="text-slate-400">Member</TableHead>
              <TableHead className="text-slate-400">Tier</TableHead>
              <TableHead className="text-slate-400">Points</TableHead>
              <TableHead className="text-slate-400">Nights</TableHead>
              <TableHead className="text-slate-400">Free Nights</TableHead>
              <TableHead className="text-slate-400">Progress</TableHead>
              <TableHead className="text-right text-slate-400">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((m) => (
              <TableRow key={m.customerId} data-testid={`loyalty-row-${m.customerId}`} className="border-slate-800 hover:bg-slate-800/50">
                <TableCell className="font-semibold text-slate-100">
                  <Link to={`/customers/${m.customerId}`} className="hover:text-[#A0148C] hover:underline">
                    {m.name}
                  </Link>
                  <span className="block font-data text-xs font-normal text-slate-500">{m.phone}</span>
                </TableCell>
                <TableCell><TierBadge tier={m.tier} /></TableCell>
                <TableCell className="font-data text-lg font-bold text-[#A0148C]">{m.pointsBalance}</TableCell>
                <TableCell className="font-data text-slate-300">{m.nightsStayed}</TableCell>
                <TableCell className="font-data text-slate-300">
                  {m.availableFreeNights > 0 ? (
                    <span className="font-bold text-emerald-400">{m.availableFreeNights} ready</span>
                  ) : (
                    <span className="text-slate-500">{m.pointsToNextFree} pts away</span>
                  )}
                  <span className="block text-xs text-slate-500">redeemed {m.freeNightsRedeemed}</span>
                </TableCell>
                <TableCell className="w-40">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
                    <div
                      className="h-full rounded-full bg-[#A0148C]"
                      style={{ width: `${Math.min(100, ((m.threshold - m.pointsToNextFree) / m.threshold) * 100)}%` }}
                    />
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    data-testid={`loyalty-redeem-${m.customerId}`}
                    size="sm"
                    disabled={m.availableFreeNights < 1 || redeeming === m.customerId || !enabled}
                    onClick={() => redeem(m.customerId)}
                    className="gap-1 bg-[#A0148C] hover:bg-[#80106F] disabled:opacity-40"
                  >
                    {redeeming === m.customerId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                    Redeem
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {members.length === 0 && (
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableCell colSpan={7} className="py-12 text-center text-slate-500">No loyalty members yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
